import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import { getContainerCharacterization } from "../../../../services/container";
import {
  aggregateMaterialDelivery,
  formatOutletData,
  sortParentCategoriesListForReport,
} from "../utils/utils";
import { repDescriptions } from "../../../../shared/utils/utils";
import { mergeMaterialFluxes } from "../utils/mergeMaterialFluxes";
import { sortObjectsInArrayByKeyName } from "../../../../components/utils/Object";
import { SIZE_RANGES } from "../../../../shared/utils/sizeRangeUtils";
import { getSettingsData } from "../../../../services/settings";
import { getEffectiveThresholdCm } from "../../characterizationDetails/constants/regulation";
import { resolvePeriodMaterialRange } from "../utils/characterizationViewMode";

const getCachedCoefficientsChoice = (cacheKey) => {
  if (!cacheKey) return false;
  try {
    const cached = localStorage.getItem("coefficientsChoices");
    if (!cached) return false;
    const choices = JSON.parse(cached);
    return Boolean(choices[cacheKey]);
  } catch (error) {
    console.warn("Error reading coefficients choices from cache:", error);
    return false;
  }
};

const saveCoefficientsChoice = (cacheKey, value) => {
  if (!cacheKey) return;
  try {
    const cached = localStorage.getItem("coefficientsChoices");
    const choices = cached ? JSON.parse(cached) : {};
    choices[cacheKey] = value;
    localStorage.setItem("coefficientsChoices", JSON.stringify(choices));
  } catch (error) {
    console.warn("Error saving coefficients choice to cache:", error);
  }
};

const computeKeptVolumeByMaterial = (materialDelivery) => {
  if (!Array.isArray(materialDelivery)) return {};
  return materialDelivery.reduce((acc, m) => {
    const key = m.cls;
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + (m.volumeObject || 0);
    return acc;
  }, {});
};

const getLatestCost = (material) => {
  const versions = material?.costVersions || [];
  if (versions.length > 0) {
    const sorted = [...versions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    return Number(sorted[0].value) || 0;
  }
  return Number(material?.cost) || 0;
};

const buildCostByCls = (materialsList) => {
  const costByCls = {};
  if (!Array.isArray(materialsList)) return costByCls;
  materialsList.forEach((mat) => {
    const value = Math.round(getLatestCost(mat) * 100) / 100;
    if (mat.referenceName) costByCls[mat.referenceName] = value;
    (mat.possibleNames || []).forEach((name) => {
      costByCls[name] = value;
    });
  });
  return costByCls;
};

const computeEconomicValue = (
  displayedMaterialDelivery,
  materialsList,
  totalWasteMass = 0,
  totalWeight = 0,
) => {
  const costByCls = buildCostByCls(materialsList);
  const useRealWeight =
    totalWasteMass > 0 && totalWeight != null && Number(totalWeight) > 0;
  const deliveryWithValue = (displayedMaterialDelivery || []).map((item) => {
    const costPerTon = costByCls[item.cls] ?? 0;
    const massKg = useRealWeight
      ? (Number(item.massObject) / totalWasteMass) * Number(totalWeight)
      : Number(item.massObject) || 0;

    const valueEur = Math.round((massKg / 1000) * costPerTon * 100) / 100;
    return { ...item, valueEur };
  });
  const totalValueEur =
    Math.round(
      deliveryWithValue.reduce((sum, item) => sum + (item.valueEur ?? 0), 0) *
        100,
    ) / 100;
  return { materialDelivery: deliveryWithValue, totalValueEur };
};

export const useCharacterizationData = ({
  period,
  container,
  superContainer,
  characterizationType = "1199",
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(Cookies.get("viewMode") || "client");
  const [sizeThreshold, setSizeThreshold] = useState(40); // Default: 40 cm, will be updated from settings

  const [historyMaterial, setHistoryMaterial] = useState([]);
  const [characterizationInProgress, setCharacterizationInProgress] =
    useState(false);
  const [materialDelivery, setMaterialDelivery] = useState([]);
  const [totalValueEur, setTotalValueEur] = useState(0);
  const [totalWasteMass, setTotalWasteMass] = useState(0);
  const [totalWasteVolume, setTotalWasteVolume] = useState(0);
  const [recoverableMaterialList, setRecoverableMaterialList] = useState([]);
  const [parentCategoriesList, setParentCategoriesList] = useState([]);
  const [outletData, setOutletData] = useState({});
  const [totalVolumeRecoverable, setTotalVolumeRecoverable] = useState(0);
  const [totalMassRecoverable, setTotalMassRecoverable] = useState(0);
  const [recoverableWithOutlet, setRecoverableWithOutlet] = useState(0);
  const [recoverableWithNoOutlet, setRecoverableWithNoOutlet] = useState(0);
  const [materialTotal, setMaterialTotal] = useState(0);
  const [repData, setRepData] = useState({});
  const [rawRepDataFromApi, setRawRepDataFromApi] = useState(null);
  const [proportion, setProportion] = useState({});
  const [sizeRangeCounts, setSizeRangeCounts] = useState(null);
  const [sizeRangeMatrix, setSizeRangeMatrix] = useState(null);
  const [totalFinesMass, setTotalFinesMass] = useState(0);
  const [totalFinesVolume, setTotalFinesVolume] = useState(0);
  const [finesBelowThreshold, setFinesBelowThreshold] = useState({
    count: 0,
    volumeObject: 0,
    massObject: 0,
    finesMass: 0,
    finesVolume: 0,
  });
  const [finesBelowThresholdByMaterial, setFinesBelowThresholdByMaterial] =
    useState({});
  const [keptVolumeByMaterialFp0, setKeptVolumeByMaterialFp0] = useState({});
  const [
    keptVolumeByMaterialFpCalculated,
    setKeptVolumeByMaterialFpCalculated,
  ] = useState({});

  const [useLatestCoefficients, setUseLatestCoefficients] = useState(false);
  const [hasNewerCoefficients, setHasNewerCoefficients] = useState(false);
  const [coefficientsVersion, setCoefficientsVersion] = useState("historical");
  const [coefficientsDate, setCoefficientsDate] = useState(null);
  const [latestCoefficientsDate, setLatestCoefficientsDate] = useState(null);
  const [toogleCharacterization, setToogleCharacterization] = useState(false);
  const previousPeriodKeyRef = useRef(null);
  const isViewModeSwitchRef = useRef(false);

  useEffect(() => {
    const handler = (event) => {
      const nextViewMode =
        event?.detail?.viewMode || Cookies.get("viewMode") || "client";
      isViewModeSwitchRef.current = true;
      setViewMode(nextViewMode);
    };

    window.addEventListener("viewModeChanged", handler);
    return () => {
      window.removeEventListener("viewModeChanged", handler);
    };
  }, []);

  const getReportCacheKey = useCallback(() => {
    const containerId = superContainer ? superContainer._id : container?._id;
    if (!period) return null;
    const { periodMaterialStart, periodMaterialStop } =
      resolvePeriodMaterialRange(period);
    return `coefficients_${containerId}_${periodMaterialStart}_${periodMaterialStop}`;
  }, [superContainer, container?._id, period]);

  const resetData = useCallback(() => {
    setHistoryMaterial([]);
    setCharacterizationInProgress(false);
    setMaterialDelivery([]);
    setTotalValueEur(0);
    setTotalWasteMass(0);
    setTotalWasteVolume(0);
    setRecoverableMaterialList([]);
    setParentCategoriesList([]);
    setTotalVolumeRecoverable(0);
    setTotalMassRecoverable(0);
    setRecoverableWithOutlet(0);
    setRecoverableWithNoOutlet(0);
    setMaterialTotal(0);
    setRepData({});
    setRawRepDataFromApi(null);
    setProportion({});
    setSizeRangeCounts(null);
    setSizeRangeMatrix(null);
    setTotalFinesMass(0);
    setTotalFinesVolume(0);
    setFinesBelowThreshold({
      count: 0,
      volumeObject: 0,
      massObject: 0,
      finesMass: 0,
      finesVolume: 0,
    });
    setFinesBelowThresholdByMaterial({});
    setKeptVolumeByMaterialFp0({});
    setKeptVolumeByMaterialFpCalculated({});
    setHasNewerCoefficients(false);
    setCoefficientsVersion("historical");
    setCoefficientsDate(null);
    setLatestCoefficientsDate(null);
  }, []);

  // When period changes, load cached coefficients choice and reset metadata
  useEffect(() => {
    const cacheKey = getReportCacheKey();
    const { periodMaterialStart, periodMaterialStop } =
      resolvePeriodMaterialRange(period);
    const currentPeriodKey =
      period && periodMaterialStart && periodMaterialStop
        ? `${periodMaterialStart}_${periodMaterialStop}`
        : null;
    if (currentPeriodKey && currentPeriodKey !== previousPeriodKeyRef.current) {
      previousPeriodKeyRef.current = currentPeriodKey;
      setUseLatestCoefficients(getCachedCoefficientsChoice(cacheKey));
      setHasNewerCoefficients(false);
      setCoefficientsVersion("historical");
      setCoefficientsDate(null);
      setLatestCoefficientsDate(null);
    }
  }, [period, getReportCacheKey]);

  const fetchCharacterizationData = useCallback(
    async (options = {}) => {
      const silent = options.silent === true;
      const debug =
        process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "true" ||
        process.env.REACT_APP_CHARACTERIZATION2_DEBUG === "1";
      const start = debug ? performance.now() : 0;
      const containerId = superContainer ? superContainer._id : container?._id;
      if (!period || !containerId) {
        resetData();
        if (!silent) setIsLoading(false);
        return;
      }
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const cacheKey = getReportCacheKey();
        const cachedChoice = getCachedCoefficientsChoice(cacheKey);
        setUseLatestCoefficients(cachedChoice);

        const { periodMaterialStart, periodMaterialStop } =
          resolvePeriodMaterialRange(period);
        if (!periodMaterialStart || !periodMaterialStop) {
          resetData();
          if (!silent) setIsLoading(false);
          return;
        }

        const baseParams = {
          periodMaterialStart,
          periodMaterialStop,
          customDate: true,
          language: i18n.language,
          characterizationType,
          sizeThreshold: getEffectiveThresholdCm(
            characterizationType,
            sizeThreshold,
          ),
          useLatestCoefficients: cachedChoice,
        };

        const isAdmin =
          (Cookies.get("userEmail") || "").includes("@akanthas.com") &&
          viewMode === "admin";

        const reqMain = getContainerCharacterization(
          { ...baseParams },
          containerId,
        );
        const arrMain = Array.isArray(reqMain) ? reqMain : [reqMain];

        const reqFp0Promise = isAdmin
          ? getContainerCharacterization(
              { ...baseParams, useFinesProportion: false },
              containerId,
            )
          : null;

        const [responsesMain, responseFp0] = await Promise.all([
          Promise.all(arrMain),
          reqFp0Promise,
        ]);

        const responses = Array.isArray(responsesMain)
          ? responsesMain
          : [responsesMain];
        const validResponses = responses.filter((r) => r?.status === 200);
        if (validResponses.length === 0) {
          resetData();
          if (!silent) setIsLoading(false);
          return;
        }
        const responseData = validResponses[0].data;

        const sortedHistory = (responseData.historyMaterial || []).sort(
          (a, b) =>
            new Date(b._time || 0).getTime() - new Date(a._time || 0).getTime(),
        );
        setHistoryMaterial(sortedHistory);
        setCharacterizationInProgress(
          Boolean(responseData.ongoingCharacterization),
        );
        // Merge materials description
        const mergeConfig = JSON.parse(
          localStorage.getItem("materialsDescription"),
        ).map((v) => ({
          referenceName: v.referenceName,
          possibleNames: v.possibleNames,
        }));

        const concatenatedMaterials = [
          ...responseData.materialDelivery
            .reduce((r, o) => {
              const sizeRange = o.sizeRange || "total";
              const key = `${o.cls}_${sizeRange}`;
              const item =
                r.get(key) ||
                Object.assign({}, o, {
                  sizeRange,
                  count: 0,
                  massObject: 0,
                  volumeObject: 0,
                  finesMass: 0,
                  finesVolume: 0,
                  surface: 0,
                  materialTotal: 0,
                  totalMassRecoverable: 0,
                  totalVolumeRecoverable: 0,
                  recoverable: false,
                });
              item.count += +(o.count || 0);
              item.massObject += +(o.massObject || 0);
              item.volumeObject += +(o.volumeObject || 0);
              item.finesMass += +(o.finesMass || 0);
              item.finesVolume += +(o.finesVolume || 0);
              item.surface += +(o.surface || 0);
              item.materialTotal += +(o.materialTotal || 0);
              item.totalMassRecoverable += +(o.totalMassRecoverable || 0);
              item.totalVolumeRecoverable += +(o.totalVolumeRecoverable || 0);
              item.recoverable =
                o.recoverable !== undefined ? o.recoverable : item.recoverable;
              item.recoverableWithOutlet =
                o.data?.outletData?.recoverableWithOutlet;
              item.recoverableWithNoOutlet =
                o.data?.outletData?.recoverableWithNoOutlet;
              return r.set(key, item);
            }, new Map())
            .values(),
        ];

        const mergedFluxes = mergeMaterialFluxes(
          concatenatedMaterials,
          mergeConfig,
        );
        const sortedFluxes = sortObjectsInArrayByKeyName(mergedFluxes, "cls");
        const displayedMaterialDelivery =
          aggregateMaterialDelivery(sortedFluxes);

        setMaterialDelivery(displayedMaterialDelivery || []);
        setKeptVolumeByMaterialFpCalculated(
          computeKeptVolumeByMaterial(displayedMaterialDelivery || []),
        );
        const materialsList = JSON.parse(
          localStorage.getItem("materialsDescription") || "[]",
        );

        const totalWasteMassFromApi = responseData.totalWasteMass || 0;
        const totalWeightKg =
          period?.weight != null ? Number(period.weight) : 0;
        const {
          materialDelivery: deliveryWithValue,
          totalValueEur: totalValueEurComputed,
        } = computeEconomicValue(
          displayedMaterialDelivery,
          materialsList,
          totalWasteMassFromApi,
          totalWeightKg,
        );

        setMaterialDelivery(deliveryWithValue || []);
        setTotalValueEur(totalValueEurComputed);
        setParentCategoriesList(
          sortParentCategoriesListForReport(
            responseData.parentCategoriesList || [],
          ),
        );
        setRecoverableMaterialList(responseData.recoverableMaterialList || []);
        setTotalWasteMass(responseData.totalWasteMass || 0);
        setTotalWasteVolume(responseData.totalWasteVolume || 0);
        setTotalMassRecoverable(responseData.totalMassRecoverable || 0);
        setTotalVolumeRecoverable(responseData.totalVolumeRecoverable || 0);
        setMaterialTotal(responseData.materialTotal || 0);
        setRecoverableWithOutlet(
          responseData?.outletData?.recoverableWithOutlet?.massTotal || 0,
        );
        setRecoverableWithNoOutlet(
          responseData?.outletData?.recoverableWithNoOutlet?.massTotal || 0,
        );

        const formattedOutletData = formatOutletData(
          responseData?.outletData,
          t,
          i18n.language,
        );
        setOutletData(formattedOutletData);

        // Stocker les données de distribution de taille
        setSizeRangeCounts(responseData.sizeRangeCounts || null);
        setSizeRangeMatrix(responseData.sizeRangeMatrix || null);

        // Stocker les données des fines
        setTotalFinesMass(responseData.totalFinesMass || 0);
        setTotalFinesVolume(responseData.totalFinesVolume || 0);
        setFinesBelowThreshold(
          responseData.finesBelowThreshold || {
            count: 0,
            volumeObject: 0,
            massObject: 0,
            finesMass: 0,
            finesVolume: 0,
          },
        );

        setFinesBelowThresholdByMaterial(
          responseData.finesBelowThresholdByMaterial || {},
        );

        if (responseFp0?.status === 200 && responseFp0?.data) {
          const fp0MaterialDelivery = responseFp0.data.materialDelivery || [];
          const fp0Concatenated = [
            ...fp0MaterialDelivery
              .reduce((r, o) => {
                const sizeRange = o.sizeRange || "total";
                const key = `${o.cls}_${sizeRange}`;
                const item =
                  r.get(key) ||
                  Object.assign({}, o, {
                    sizeRange,
                    count: 0,
                    massObject: 0,
                    volumeObject: 0,
                    finesMass: 0,
                    finesVolume: 0,
                    surface: 0,
                    materialTotal: 0,
                    totalMassRecoverable: 0,
                    totalVolumeRecoverable: 0,
                    recoverable: false,
                  });
                item.count += +(o.count || 0);
                item.massObject += +(o.massObject || 0);
                item.volumeObject += +(o.volumeObject || 0);
                item.finesMass += +(o.finesMass || 0);
                item.finesVolume += +(o.finesVolume || 0);
                item.surface += +(o.surface || 0);
                item.materialTotal += +(o.materialTotal || 0);
                item.totalMassRecoverable += +(o.totalMassRecoverable || 0);
                item.totalVolumeRecoverable += +(o.totalVolumeRecoverable || 0);
                item.recoverable =
                  o.recoverable !== undefined
                    ? o.recoverable
                    : item.recoverable;
                item.recoverableWithOutlet =
                  o.data?.outletData?.recoverableWithOutlet;
                item.recoverableWithNoOutlet =
                  o.data?.outletData?.recoverableWithNoOutlet;
                return r.set(key, item);
              }, new Map())
              .values(),
          ];
          const fp0Merged = mergeMaterialFluxes(fp0Concatenated, mergeConfig);
          const fp0Sorted = sortObjectsInArrayByKeyName(fp0Merged, "cls");
          const fp0Displayed = aggregateMaterialDelivery(fp0Sorted);
          setKeptVolumeByMaterialFp0(
            computeKeptVolumeByMaterial(fp0Displayed || []),
          );
        } else {
          setKeptVolumeByMaterialFp0({});
        }

        // Coefficients version metadata from API
        if (responseData && typeof responseData === "object") {
          setHasNewerCoefficients(Boolean(responseData.hasNewerCoefficients));
          setCoefficientsVersion(
            responseData.coefficientsVersion === "latest" ||
              responseData.coefficientsVersion === "historical"
              ? responseData.coefficientsVersion
              : cachedChoice
                ? "latest"
                : "historical",
          );
          setCoefficientsDate(responseData.coefficientsDate || null);
          setLatestCoefficientsDate(
            responseData.latestCoefficientsDate || null,
          );
        } else {
          setHasNewerCoefficients(false);
          setCoefficientsVersion(cachedChoice ? "latest" : "historical");
          setCoefficientsDate(null);
          setLatestCoefficientsDate(null);
        }

        // Stocker les données brutes de repData pour recalcul ultérieur
        const newRepData = validResponses
          .map((r) => r.data?.repData)
          .filter(Boolean);
        if (newRepData.length > 0) {
          setRawRepDataFromApi(newRepData);
        } else {
          setRawRepDataFromApi(null);
        }

        // Calculer les proportions si superContainer existe
        if (superContainer && responseData.yellow_surface_to_reference_ratio) {
          const yellowRatios = Array.isArray(
            responseData.yellow_surface_to_reference_ratio,
          )
            ? responseData.yellow_surface_to_reference_ratio
            : [];

          const flatRatios = Array.isArray(yellowRatios)
            ? yellowRatios.flat()
            : [];

          // eslint-disable-next-line no-console
          console.log("[char2] yellow_surface_to_reference_ratio", {
            superContainer: superContainer?._id || superContainer,
            raw: responseData.yellow_surface_to_reference_ratio,
            flatRatios,
            invalidItems: flatRatios.filter((item) => {
              if (!item || typeof item !== "object") return true;
              const entry = Object.entries(item)[0];
              return !entry || entry[1] == null;
            }),
          });

          const totalProportion = flatRatios.reduce((acc, curr) => {
            const value = Object.values(curr)[0];
            return acc + (parseFloat(value) || 0);
          }, 0);

          const proportionData = flatRatios.reduce((acc, item) => {
            const [containerId, value] = Object.entries(item)[0];
            acc[containerId] =
              totalProportion > 0
                ? parseFloat(value / totalProportion).toFixed(4)
                : 0;
            return acc;
          }, {});

          setProportion(proportionData);
        } else {
          setProportion({});
        }
        if (debug) {
          // eslint-disable-next-line no-console
          console.log(
            "[char2] useCharacterizationData fetched in",
            `${(performance.now() - start).toFixed(0)}ms`,
            {
              containerId,
              periodStart: periodMaterialStart,
              periodStop: periodMaterialStop,
            },
          );
        }
      } catch (err) {
        console.error(
          "Erreur lors du chargement des données de caractérisation:",
          err,
        );
        setError(err);
        resetData();
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [
      period,
      container,
      superContainer,
      characterizationType,
      sizeThreshold,
      i18n.language,
      viewMode,
      resetData,
      t,
      getReportCacheKey,
    ],
  );

  useEffect(() => {
    if (isViewModeSwitchRef.current) {
      isViewModeSwitchRef.current = false;
      fetchCharacterizationData({ silent: true });
      return;
    }
    fetchCharacterizationData();
  }, [fetchCharacterizationData, toogleCharacterization]);

  const onCoefficientsToggle = useCallback(
    (value) => {
      setUseLatestCoefficients(value);
      const cacheKey = getReportCacheKey();
      saveCoefficientsChoice(cacheKey, value);
      setToogleCharacterization((prev) => !prev);
    },
    [getReportCacheKey],
  );

  // Calculer repData selon characterizationType sans refaire le fetch
  useEffect(() => {
    if (characterizationType === "EPR" && rawRepDataFromApi) {
      const mergedRep = rawRepDataFromApi.reduce(
        (acc, item) => {
          if (!item || !item.data) return acc;
          acc.volumeTotal += item.volumeTotal || 0;
          acc.massTotal += item.massTotal || 0;
          Object.entries(item.data).forEach(([rep, values]) => {
            const repKey = repDescriptions[rep]?.category || rep;
            if (!acc.data[repKey]) {
              acc.data[repKey] = {
                massObject: 0,
                volumeObject: 0,
                objects: [],
              };
            }
            acc.data[repKey].massObject += values.massObject || 0;
            acc.data[repKey].volumeObject += values.volumeObject || 0;
            acc.data[repKey].objects = [
              ...new Set([
                ...acc.data[repKey].objects,
                ...(values.objects || []),
              ]),
            ];
          });
          return acc;
        },
        {
          volumeTotal: 0,
          massTotal: 0,
          data: {
            "PMCB Category 1": {
              massObject: 0,
              volumeObject: 0,
              objects: [],
            },
            "PMCB Category 2": {
              massObject: 0,
              volumeObject: 0,
              objects: [],
            },
            ABJ: {
              massObject: 0,
              volumeObject: 0,
              objects: [],
              description: t("Articles de bricolage et de jardin"),
            },
            ALS: {
              massObject: 0,
              volumeObject: 0,
              objects: [],
              description: t("Articles de loisirs et de sport"),
            },
            D3E: {
              massObject: 0,
              volumeObject: 0,
              objects: [],
              description: t(
                "Responsabilité Élargie du Producteur pour les Déchets d'Équipements Électriques et Électroniques",
              ),
            },
          },
        },
      );

      const repChartLabels = Object.keys(mergedRep.data).sort();
      const repChartData = repChartLabels
        .filter((key) => mergedRep.data[key].volumeObject > 0)
        .map((key) => ({
          name: t(key),
          value: (
            (mergedRep.data[key].volumeObject / mergedRep.volumeTotal) *
            100
          ).toFixed(1),
        }));
      setRepData({
        data: mergedRep.data,
        repChartData,
        repChartLabels: repChartLabels.map((key) => t(key)),
        volumeTotal: mergedRep.volumeTotal,
      });
    } else {
      setRepData({});
    }
  }, [characterizationType, rawRepDataFromApi, t]);

  // Load size threshold from settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettingsData();
        if (response.data?.settings?.finesSizeThreshold !== undefined) {
          setSizeThreshold(response.data.settings.finesSizeThreshold);
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        // Keep default value of 40 if settings fail to load
      }
    };
    loadSettings();
  }, []);

  // Calculer la distribution de taille
  const sizeRangeDistribution = useMemo(() => {
    const distribution = new Map();
    SIZE_RANGES.forEach((range) => {
      distribution.set(range, 0);
    });

    if (sizeRangeCounts) {
      Object.entries(sizeRangeCounts).forEach(([range, count]) => {
        if (!distribution.has(range)) return;
        distribution.set(range, (distribution.get(range) || 0) + (count || 0));
      });
    } else {
      // Fallback: derive from materialDelivery (may exclude objects below threshold)
      materialDelivery.forEach((material) => {
        const range = material.sizeRange || "total";
        const count = distribution.get(range) || 0;
        distribution.set(range, count + (material.count || 0));
      });
    }

    return {
      labels: Array.from(distribution.keys()),
      data: Array.from(distribution.values()),
    };
  }, [materialDelivery, sizeRangeCounts]);

  return {
    isLoading,
    error,
    totalValueEur,
    historyMaterial,
    characterizationInProgress,
    materialDelivery,
    totalWasteMass,
    totalWasteVolume,
    recoverableMaterialList,
    parentCategoriesList,
    outletData,
    totalVolumeRecoverable,
    totalMassRecoverable,
    recoverableWithOutlet,
    recoverableWithNoOutlet,
    materialTotal,
    repData,
    proportion,
    sizeRangeDistribution,
    sizeRangeMatrix,
    sizeThreshold,
    totalFinesMass,
    totalFinesVolume,
    finesBelowThreshold,
    finesBelowThresholdByMaterial,
    keptVolumeByMaterialFp0,
    keptVolumeByMaterialFpCalculated,

    refetch: fetchCharacterizationData,
    reset: resetData,

    useLatestCoefficients,
    hasNewerCoefficients,
    coefficientsVersion,
    coefficientsDate,
    latestCoefficientsDate,
    onCoefficientsToggle,
  };
};
